import React, { useState } from 'react';
import { useTeamStore } from '../store/teamStore';
import { useGameStore } from '../store/gameStore';
import { useAdminStore } from '../store/adminStore';
import { Users, Play, Settings, ShieldAlert, Check } from 'lucide-react';

export const TeamLobby: React.FC = () => {
  const { teams, initializeTeams, setTeamReady } = useTeamStore();
  const { enterBriefing } = useGameStore();
  const { toggleDashboard } = useAdminStore();
  const [playerCount, setPlayerCount] = useState(2);
  const [showSaved, setShowSaved] = useState(false);

  const handleStart = () => {
    initializeTeams(playerCount);
    enterBriefing();
  };

  const handleSettingChange = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 2000);
  };

  return (
    <div className="fixed inset-0 bg-lobby-pattern z-50 flex items-center justify-center">
      <div className="bg-slate-900/95 backdrop-blur border border-slate-700 rounded-xl p-8 max-w-4xl w-full shadow-2xl relative">
        <button 
          onClick={toggleDashboard}
          className="absolute top-8 right-8 p-2 text-slate-500 hover:text-ops-blue hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-2"
          title="Open Game Manager"
        >
          <ShieldAlert size={20} />
          <span className="text-sm font-medium">Admin</span>
        </button>

        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-white mb-2 tracking-widest">WAR ROOM LOBBY</h1>
          <p className="text-slate-400">Configure teams and mission parameters</p>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-300 flex items-center gap-2">
              <Users className="w-5 h-5" /> Teams
            </h3>
            <div className="space-y-2">
              {Array.from({ length: playerCount }).map((_, idx) => (
                <div key={idx} className="bg-slate-800 p-4 rounded border border-slate-700 flex justify-between items-center hover:bg-slate-700 transition-colors cursor-default group">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${idx === 0 ? 'bg-blue-500' : 'bg-red-500'}`} />
                    <span className="text-slate-200 font-medium group-hover:text-white transition-colors">
                      {idx === 0 ? 'Blue Team (You)' : 'Red Team (AI)'}
                    </span>
                  </div>
                  <span className="text-xs bg-slate-700 px-2 py-1 rounded text-slate-400 group-hover:bg-slate-600 group-hover:text-slate-200 transition-colors">
                    {idx === 0 ? 'Commander' : 'Adversary'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4 relative z-10">
            <h3 className="text-lg font-semibold text-slate-300 flex items-center gap-2">
              <Settings className="w-5 h-5" /> Settings
            </h3>
            <div className="bg-slate-800 p-6 rounded border border-slate-700 space-y-4 relative">
              {showSaved && (
                <div className="absolute top-2 right-2 flex items-center gap-1 text-green-400 text-xs animate-pulse">
                  <Check size={12} /> Saved
                </div>
              )}
              <div>
                <label className="block text-sm text-slate-400 mb-2">Difficulty</label>
                <select 
                  onChange={handleSettingChange}
                  className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-slate-200 focus:ring-2 focus:ring-ops-blue outline-none transition-all"
                >
                  <option>Standard</option>
                  <option>Hard</option>
                  <option>Simulation (Extreme)</option>
                </select>
              </div>
              <div className="relative">
                <label className="block text-sm text-slate-400 mb-2">Turn Timer</label>
                <select 
                  onChange={handleSettingChange}
                  className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-slate-200 focus:ring-2 focus:ring-ops-blue outline-none transition-all"
                >
                  <option>25 Minutes</option>
                  <option>60 Minutes</option>
                  <option>Unlimited</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center">
          <button 
            onClick={handleStart}
            className="bg-ops-blue hover:bg-blue-600 text-white px-12 py-4 rounded-lg font-bold text-lg tracking-wider shadow-lg shadow-blue-900/20 hover:shadow-blue-900/40 transition-all flex items-center gap-3 active:scale-95"
          >
            <Play fill="currentColor" />
            DEPLOY TO THEATER
          </button>
        </div>
      </div>
    </div>
  );
};